import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken } from '../utils/auth';
import * as audioCache from '../utils/offline/audioCache';
import * as repository from '../utils/offline/repository';
import { useOffline } from '../OfflineProvider';

const formatBytes = (bytes) => {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${Math.round(bytes / 1024)} KB` : `${mb.toFixed(1)} MB`;
};

/**
 * Offline availability for one project.
 *
 * The only part of project settings that still makes sense with no network,
 * which is why it is also the only part rendered then: everything else on this
 * page talks to the server.
 */
const ProjectOfflineSection = ({ projectId }) => {
  const { online } = useOffline();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const all = (await audioCache.listDownloads()) || [];
    setRows(all.filter((r) => r.projectId === projectId));
  }, [projectId]);

  useEffect(() => {
    refresh();
    return audioCache.subscribe(refresh);
  }, [refresh]);

  const ready = rows.filter((r) => r.state === audioCache.READY);
  const bytes = ready.reduce((sum, r) => sum + (r.bytes || 0), 0);

  const chaptersFor = useCallback(async () => {
    // Prefer the copy already on the device; fall back to the server, which is
    // reachable because this action is only offered online.
    const cached = await repository.getChapters(projectId).catch(() => []);
    if (cached.length > 0) return cached;

    const token = getAccessToken();
    const response = await axios.get(getApiUrl(`projects/${projectId}/chapters`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.chapters || [];
  }, [projectId]);

  const downloadAll = async () => {
    setBusy(true);
    setError('');
    try {
      const chapters = await chaptersFor();
      const { total } = await audioCache.downloadProject(projectId, chapters, (done, count) =>
        setProgress({ done, count }));

      if (total === 0) {
        setError('This project has no generated audio yet.');
        return;
      }

      // Individual chapters fail without throwing, and the chapter rows only
      // mark what succeeded — so a partial download would otherwise look like
      // a complete one.
      const after = (await audioCache.listDownloads()) || [];
      const failed = after.filter((r) => r.projectId === projectId && r.state === audioCache.FAILED);
      if (failed.length > 0) {
        setError(
          `${failed.length} of ${total} chapter${total === 1 ? '' : 's'} could not be downloaded. `
          + 'Try again to pick up the rest.',
        );
      }
    } catch (e) {
      setError('Could not download this project. Check your connection and try again.');
    } finally {
      setBusy(false);
      setProgress(null);
      refresh();
    }
  };

  const removeAll = async () => {
    setBusy(true);
    try {
      await audioCache.removeProjectDownloads(projectId);
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
        Offline
      </h2>
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
        {ready.length > 0 ? (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {ready.length} chapter{ready.length === 1 ? '' : 's'} on this device, using {formatBytes(bytes)}.
            </p>
            <button
              type="button"
              onClick={removeAll}
              disabled={busy}
              className="w-full rounded-lg border border-red-300 px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              {busy ? 'Removing…' : 'Delete offline copy'}
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The project stays in your library — only the downloaded audio is removed.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Download every chapter so this project plays with no connection.
            </p>
            <button
              type="button"
              onClick={downloadAll}
              disabled={busy || !online}
              className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {progress ? `Downloading ${progress.done} of ${progress.count}…` : 'Download all'}
            </button>
          </>
        )}
        {error && <p role="note" className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </section>
  );
};

export default ProjectOfflineSection;
